import type { QueryClient } from "@tanstack/react-query";

import type { JobRun } from "@/hooks/useActivity";

/** What a person calls each job kind (job_runs.kind). */
export const KIND_LABEL: Record<string, string> = {
  scoring: "Scoring",
  pdf: "PDF retrieval",
  monitor: "Living Lit check",
  bibtex: "BibTeX import",
  seed_discovery: "Seed discovery",
  smart_trawl: "Spot Trawl",
  field_debate: "Field Debate",
  field_debate_refresh: "Field Debate refresh",
  coverage_audit: "Coverage Audit",
  gap_fill: "Gap Fill",
  zotero_import: "Zotero import",
  refine_rescore: "Rescoring after a refine",
  chat_index: "Chat index",
  trawl: "Trawl",
  snowball: "Snowball",
  abstract_backfill: "Filling in abstracts",
};

export const kindLabel = (kind: string) => KIND_LABEL[kind] ?? kind;

/** The bar's op type for a kind whose store shows it live in this tab. */
export const STORE_OP: Record<string, string> = {
  scoring: "scoring",
  pdf: "pdf",
  monitor: "monitor",
  bibtex: "bib-import",
  seed_discovery: "seed-discovery",
  smart_trawl: "smart-trawl",
  field_debate: "field-debate",
  field_debate_refresh: "field-debate",
  coverage_audit: "coverage-audit",
  gap_fill: "gap-fill",
  zotero_import: "zotero-bulk-import",
  trawl: "trawl",
  snowball: "snowball",
};

/** Kinds the server can stop from a pill (api/jobs.py CANCELLERS). */
export const CANCELLABLE = new Set([
  "scoring", "pdf", "monitor", "field_debate", "field_debate_refresh",
  "coverage_audit", "gap_fill", "zotero_import",
]);

/** Kinds whose stopped run can be started again from its saved request. */
export const RUN_AGAIN_PATH: Record<string, string> = {
  scoring: "scoring/start",
  pdf: "pdf/retrieve",
  monitor: "monitor/start",
};

/** Kinds that resume from where the page stopped. */
export const RESUMABLE = new Set(["trawl", "snowball"]);

/** Kinds whose finished rows the 1b sources already describe better
 * (monitor runs with their finds, FD landings, abstract fill-ins). Their
 * ledger rows only show when the run was stopped. */
export const LEDGER_ROWS_ONLY_WHEN_STOPPED = new Set([
  "monitor", "field_debate", "field_debate_refresh", "abstract_backfill",
]);

/** What to refresh once a job this tab didn't watch ends. */
export function invalidateAfter(qc: QueryClient, conceptId: string, kind: string): void {
  qc.invalidateQueries({ queryKey: ["papers", conceptId] });
  qc.invalidateQueries({ queryKey: ["paper-keys", conceptId] });
  qc.invalidateQueries({ queryKey: ["concepts", conceptId] });
  qc.invalidateQueries({ queryKey: ["papers-count", conceptId] });
  if (kind === "field_debate" || kind === "field_debate_refresh") {
    qc.invalidateQueries({ queryKey: ["field-debate", conceptId] });
  }
  if (kind === "coverage_audit" || kind === "gap_fill") {
    qc.invalidateQueries({ queryKey: ["audit", conceptId] });
  }
  if (kind === "chat_index") qc.invalidateQueries({ queryKey: ["index-status", conceptId] });
  if (kind === "monitor") qc.invalidateQueries({ queryKey: ["monitor-digest", conceptId] });
}

export const isActive = (run: JobRun) => run.status === "running" || run.status === "waiting";

/** Seeds a stopped trawl/snowball still has to follow. */
export function remainingSeeds(run: JobRun): string[] {
  const all = (run.params.seed_ids as string[] | undefined) ?? [];
  const done = new Set(run.progress.done_seed_ids ?? []);
  return all.filter((id) => !done.has(id));
}
